import type { FilingStatus } from '@/types/loanCalculationTypes';

// --- Poverty Guideline Constants (Based on 2025 FPL from incomeCalculations.gs) ---
const FPL_BASE: Record<number, number> = {
  1: 15650,
  2: 21150,
  3: 26650,
  4: 32150,
  5: 37650,
  6: 43150,
  7: 48650,
  8: 54150,
};
const FPL_PER_PERSON_ADDITION_BASE = 5500;

const FPL_AK: Record<number, number> = {
  1: 19550,
  2: 26430,
  3: 33310,
  4: 40190,
  5: 47070,
  6: 53950,
  7: 60830,
  8: 67710,
};
const FPL_PER_PERSON_ADDITION_AK = 6880;

const FPL_HI: Record<number, number> = {
  1: 17990,
  2: 24320,
  3: 30650,
  4: 36980,
  5: 43310,
  6: 49640,
  7: 55970,
  8: 62300,
};
const FPL_PER_PERSON_ADDITION_HI = 6330;

const MAX_FAMILY_SIZE_TABLE = 8;

/**
 * Gets the Federal Poverty Line (FPL) for a given state and family size.
 * Uses the 2025 guidelines.
 * @param state The state abbreviation (e.g., 'CA', 'AK') or full name. Case-insensitive.
 * @param familySize The number of people in the household.
 * @returns The annual FPL amount, or null if familySize is invalid or state lookup fails unexpectedly.
 */
export function getPovertyGuideline(state: string, familySize: number): number | null {
  let table: Record<number, number>;
  let perPersonAddition: number;

  const upperCaseState = state ? state.toUpperCase() : '';

  if (upperCaseState === 'AK' || upperCaseState === 'ALASKA') {
    table = FPL_AK;
    perPersonAddition = FPL_PER_PERSON_ADDITION_AK;
  } else if (upperCaseState === 'HI' || upperCaseState === 'HAWAII') {
    table = FPL_HI;
    perPersonAddition = FPL_PER_PERSON_ADDITION_HI;
  } else {
    table = FPL_BASE;
    perPersonAddition = FPL_PER_PERSON_ADDITION_BASE;
  }

  if (familySize <= 0) {
    console.warn('[Poverty Guideline] Invalid family size:', familySize);
    return null;
  }

  if (familySize <= MAX_FAMILY_SIZE_TABLE) {
    const fpl = table[familySize];
    return fpl;
  } else {
    const baseAmount = table[MAX_FAMILY_SIZE_TABLE];
    const additionalPersons = familySize - MAX_FAMILY_SIZE_TABLE;
    return baseAmount + additionalPersons * perPersonAddition;
  }
}

// --- ICR Factor Calculation (from paymentHelpers.gs) ---
interface IcrFactorEntry {
  agi: number;
  factor: number;
}

const ICR_FACTOR_TABLE_SINGLE: IcrFactorEntry[] = [
  { agi: 13736, factor: 0.55 },
  { agi: 18900, factor: 0.5779 },
  { agi: 24319, factor: 0.6057 },
  { agi: 29861, factor: 0.6623 },
  { agi: 35153, factor: 0.7189 },
  { agi: 41828, factor: 0.8033 },
  { agi: 52536, factor: 0.8877 },
  { agi: 65890, factor: 1.0 },
  { agi: 79279, factor: 1.0 },
  { agi: 95245, factor: 1.118 },
  { agi: 121958, factor: 1.235 },
  { agi: 172734, factor: 1.412 },
  { agi: 198056, factor: 1.5 },
  { agi: 352771, factor: 2.0 },
];

const ICR_FACTOR_TABLE_MARRIED_HOH: IcrFactorEntry[] = [
  { agi: 13736, factor: 0.5052 },
  { agi: 21672, factor: 0.5668 },
  { agi: 25826, factor: 0.5956 },
  { agi: 33764, factor: 0.6779 },
  { agi: 41828, factor: 0.7522 },
  { agi: 52536, factor: 0.8761 },
  { agi: 65889, factor: 1.0 },
  { agi: 79249, factor: 1.0 },
  { agi: 99285, factor: 1.094 },
  { agi: 132667, factor: 1.25 },
  { agi: 179409, factor: 1.406 },
  { agi: 250911, factor: 1.5 },
  { agi: 410007, factor: 2.0 },
];

/**
 * Calculates the Income Contingent Repayment (ICR) factor.
 * @param annualGrossIncome The borrower's pre-adjusted Annual Gross Income (AGI).
 * @param filingStatus The borrower's filing status. Used ONLY to select the correct factor table.
 * @param yearIndex The 0-based index of the current simulation year.
 * @param inflationRate The annual inflation rate (as a decimal).
 * @returns The calculated ICR factor. Returns 1.0 if calculation fails.
 */
export function calculateIcrFactor(
  annualGrossIncome: number,
  filingStatus: FilingStatus, // Using FilingStatus from loanCalculationTypes
  yearIndex: number,
  inflationRate: number
): number {
  const baseAgiTable: IcrFactorEntry[] =
    filingStatus === 'MFJ' || filingStatus === 'MFS' || filingStatus === 'HoH'
      ? ICR_FACTOR_TABLE_MARRIED_HOH
      : ICR_FACTOR_TABLE_SINGLE;

  if (annualGrossIncome < 0 || yearIndex < 0 || inflationRate < 0) {
    console.warn('[ICR Factor Calc] Invalid input: AGI, YearIdx, or Inflation Rate < 0');
    return 1.0;
  }

  const inflationMultiplier = yearIndex > 0 ? Math.pow(1 + inflationRate, yearIndex) : 1;
  if (!isFinite(inflationMultiplier)) {
    console.warn('[ICR Factor Calc] Invalid inflation multiplier');
    return 1.0;
  }

  const inflatedTable: IcrFactorEntry[] = baseAgiTable.map((entry) => ({
    agi: entry.agi * inflationMultiplier,
    factor: entry.factor,
  }));

  if (inflatedTable.length === 0) {
    console.warn('[ICR Factor Calc] ICR AGI table is empty for status', filingStatus);
    return 1.0;
  }

  if (annualGrossIncome <= inflatedTable[0].agi) {
    return inflatedTable[0].factor;
  }
  if (annualGrossIncome >= inflatedTable[inflatedTable.length - 1].agi) {
    return inflatedTable[inflatedTable.length - 1].factor;
  }

  let lowerBound = inflatedTable[0];
  let upperBound = inflatedTable[inflatedTable.length - 1];

  for (let i = 1; i < inflatedTable.length; i++) {
    if (annualGrossIncome <= inflatedTable[i].agi) {
      lowerBound = inflatedTable[i - 1];
      upperBound = inflatedTable[i];
      break;
    }
  }

  const agiRange = upperBound.agi - lowerBound.agi;
  const factorRange = upperBound.factor - lowerBound.factor;

  if (agiRange === 0) {
    console.warn('[ICR Factor Calc] AGI range for interpolation is zero.');
    return lowerBound.factor;
  }

  const interpolationRatio = (annualGrossIncome - lowerBound.agi) / agiRange;
  const interpolatedFactor = lowerBound.factor + interpolationRatio * factorRange;

  if (!isFinite(interpolatedFactor)) {
    console.warn('[ICR Factor Calc] Interpolation resulted in invalid number:', interpolatedFactor);
    return 1.0;
  }
  return interpolatedFactor;
}
